import {
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  HStack,
  VStack,
  Divider,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { useInteractable } from '../../../classes/TownController';
import useTownController from '../../../hooks/useTownController';
import { ColorRadio, CustomCheckboxGroup, LabelRadio } from './CustomButtons';
import AvatarFeatures from '../../../classes/AvatarFeatures';
import LocalStorage from '../../../classes/LocalStorage/Closet';
import { shuffle } from '../../../Utils';

const SKIN_COLOR_OPTIONS = AvatarFeatures.SKIN_COLOR_OPTIONS;
const HAIR_COLOR_OPTIONS = AvatarFeatures.HAIR_COLOR_OPTIONS;
const HAIR_STYLE_OPTIONS = AvatarFeatures.HAIR_STYLE_OPTIONS;
const ACCESSORY_OPTIONS = AvatarFeatures.ACCESSORY_OPTIONS;

const SOURCE_IMG_WIDTH = 32;
const SOURCE_IMG_HEIGHT = 43;
const SCALE = 3;
const PREVIEW_IMG_WIDTH = SOURCE_IMG_WIDTH * SCALE;
const PREVIEW_IMG_HEIGHT = SOURCE_IMG_HEIGHT * SCALE;

export default function ClosetAreaModal(): JSX.Element {
  const coveyTownController = useTownController();
  const newClosetSession = useInteractable('closetArea');

  const ourPlayerAvatar = coveyTownController.ourPlayer.playerFeatures;
  const [skinColor, setSkinColor] = useState<string>(ourPlayerAvatar.skinColor);
  const [hairStyle, setHairStyle] = useState<string>(ourPlayerAvatar.baseSprite);
  const [hairColor, setHairColor] = useState<string>(ourPlayerAvatar.hairColor);
  const [accessories, setAccessories] = useState<string[]>(ourPlayerAvatar.accessories);
  const [random, setRandom] = useState(false);

  const isOpen = newClosetSession !== undefined;

  const [previewContainer, setPreviewContainer] = useState<HTMLDivElement>();
  const previewRef = useCallback(node => {
    setPreviewContainer(node);
  }, []);

  const previewContainerStyle: React.CSSProperties = {
    width: PREVIEW_IMG_WIDTH,
    height: PREVIEW_IMG_HEIGHT,
  };

  useEffect(() => {
    if (newClosetSession) {
      coveyTownController.pause();
    } else {
      coveyTownController.unPause();
    }
  }, [coveyTownController, newClosetSession]);

  useEffect(() => {
    if (random) {
      const newFeatures = new AvatarFeatures(hairStyle, hairColor, skinColor, accessories);
      coveyTownController.emitAvatarChange(newFeatures);
      LocalStorage.selectedOptions = newFeatures;
      setRandom(false);
    }
    // creates the preview every time an option is clicked
    if (isOpen) {
      previewContainer?.replaceChildren();
      let images = ['player_short'].concat(accessories);
      if (hairStyle === 'long') {
        images = ['player_long'];
      }
      images.push(hairStyle + '_skin_' + skinColor);
      images.push(hairStyle + '_hair_' + hairColor);
      images = images.concat(accessories);

      const canvas = document.createElement('canvas');
      canvas.width = PREVIEW_IMG_WIDTH;
      canvas.height = PREVIEW_IMG_HEIGHT;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.imageSmoothingEnabled = false;
        images.forEach(element => {
          const img = newClosetSession?.scene.textures
            .get(element)
            .getSourceImage() as HTMLImageElement;
          if (ctx) {
            ctx.drawImage(
              img,
              SOURCE_IMG_WIDTH,
              0,
              SOURCE_IMG_WIDTH,
              SOURCE_IMG_HEIGHT,
              0,
              0,
              PREVIEW_IMG_WIDTH,
              PREVIEW_IMG_HEIGHT,
            );
            previewContainer?.appendChild(canvas);
          }
        });
      }
    }
  }, [
    accessories,
    coveyTownController,
    hairColor,
    hairStyle,
    isOpen,
    newClosetSession?.scene.textures,
    previewContainer,
    skinColor,
    random,
  ]);

  const closeModal = useCallback(() => {
    if (newClosetSession) {
      coveyTownController.interactEnd(newClosetSession);
    }
  }, [coveyTownController, newClosetSession]);

  const changeSkin = (skinHexCode: string) => {
    switch (skinHexCode) {
      case SKIN_COLOR_OPTIONS[1]: {
        setSkinColor('light');
        break;
      }
      case SKIN_COLOR_OPTIONS[2]: {
        setSkinColor('tan');
        break;
      }
      case SKIN_COLOR_OPTIONS[3]: {
        setSkinColor('dark');
        break;
      }
      // Pale skin is the default sprite
      default: {
        setSkinColor('pale');
      }
    }
  };

  const changeHair = (hairHexCode: string) => {
    switch (hairHexCode) {
      case HAIR_COLOR_OPTIONS[0]: {
        setHairColor('black');
        break;
      }
      case HAIR_COLOR_OPTIONS[1]: {
        setHairColor('red');
        break;
      }
      case HAIR_COLOR_OPTIONS[2]: {
        setHairColor('white');
        break;
      }
      // Blonde hair is the default sprite
      default: {
        setHairColor('blonde');
      }
    }
  };

  // Produces and saves a random avatar configuration for the current player
  const randomize = () => {
    const randomHairColor = Math.floor(Math.random() * HAIR_COLOR_OPTIONS.length);
    changeHair(HAIR_COLOR_OPTIONS[randomHairColor]);

    const randomSkinColor = Math.floor(Math.random() * SKIN_COLOR_OPTIONS.length);
    changeSkin(SKIN_COLOR_OPTIONS[randomSkinColor]);

    const randomBase = Math.floor(Math.random() * 2);
    if (randomBase === 0) {
      setHairStyle('long');
    } else {
      setHairStyle('short');
    }

    const numOfAccessories = Math.floor(Math.random() * (ACCESSORY_OPTIONS.length + 1));
    if (numOfAccessories !== 0) {
      const shuffledAccessories = shuffle(ACCESSORY_OPTIONS);
      const randomAccessories = [];
      for (let i = 0; i < numOfAccessories; i++) {
        randomAccessories[i] = shuffledAccessories[i];
      }
      setAccessories(randomAccessories);
    } else {
      setAccessories([]);
    }

    setRandom(true);
    closeModal();
  };

  // Updates the avatar with the selected features from the modal.
  const updateAvatar = useCallback(async () => {
    const newFeatures = new AvatarFeatures(hairStyle, hairColor, skinColor, accessories);
    coveyTownController.emitAvatarChange(newFeatures);
    LocalStorage.selectedOptions = newFeatures;
    closeModal();
  }, [coveyTownController, hairStyle, hairColor, skinColor, accessories, closeModal]);

  return (
    <Modal
      size='2xl'
      isOpen={isOpen}
      onClose={() => {
        closeModal();
        coveyTownController.unPause();
      }}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>My Closet </ModalHeader>
        <ModalCloseButton />
        <form
          onSubmit={ev => {
            ev.preventDefault();
          }}>
          <ModalBody pb={6}>
            <FormControl>
              <HStack>
                <div ref={previewRef} style={previewContainerStyle}></div>

                <VStack>
                  <FormLabel>Skin Color</FormLabel>
                  <ColorRadio
                    name='skin-color'
                    value={skinColor}
                    onChange={e => changeSkin(e)}
                    options={SKIN_COLOR_OPTIONS}></ColorRadio>

                  <FormLabel>Hair Style</FormLabel>
                  <LabelRadio
                    name='hair-style'
                    value={hairStyle}
                    onChange={e => setHairStyle(e)}
                    options={HAIR_STYLE_OPTIONS}
                  />
                </VStack>

                <Divider />

                <VStack>
                  <FormLabel>Hair Color</FormLabel>
                  <ColorRadio
                    name='hair-color'
                    value={hairColor}
                    onChange={e => changeHair(e)}
                    options={HAIR_COLOR_OPTIONS}></ColorRadio>

                  <FormLabel>Accessories</FormLabel>
                  <CustomCheckboxGroup
                    value={accessories}
                    onChange={e => setAccessories(e)}
                    options={ACCESSORY_OPTIONS}
                  />
                </VStack>
              </HStack>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button mr='auto' colorScheme='blue' onClick={randomize}>
              Randomize
            </Button>
            <Button colorScheme='blue' mr={3} onClick={updateAvatar}>
              Finish
            </Button>
            <Button onClick={closeModal}>Cancel</Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
